import PropTypes from 'prop-types';
import {Children, cloneElement, useState} from 'react';
import Tab from './Tab/Tab';
import './TabbedContent.scss';
import TabContent from './TabContent/TabContent';
import TabTitle from './TabTitle/TabTitle';

/**
 * = Description =
 * Component to render tabbed content
 *
 * Each Tab entry will render a Tab title in the top row,
 * and when that tab is selected its corresponding content will be rendered
 *
 * = Example usage =
 *
 * <TabbedContent>
 *   <Tab>
 *     <TabTitle>PayPal</TabTitle>
 *     <TabContent>
 *       PayPal is a payment method
 *     </TabContent>
 *   </Tab>
 *   <Tab>
 *     <TabTitle>Klarna</TabTitle>
 *     <TabContent>
 *       <KlarnaHelpInfoMessageDetailContainer/>
 *     </TabContent>
 *   </Tab>
 * </TabbedContent>
 *
 * = Props list =
 * children: all top-level elements of its children must be a Tab element, otherwise it's ignored
 *
 * = Common issues =
 * Don't forget to import all of those classes!
 *
 */

function TabbedContent(props) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onTabSelect = (index) => {
    setSelectedIndex(index);
  };
  
  const getSubcomponent = (tab, componentType) => {
    const tabSubcomponents = Children.toArray(tab.props.children);
    return tabSubcomponents.find(tabSubcomponent => tabSubcomponent.type === componentType);
  };

  const getAllSubcomponentsOfType = (componentType) => {
    const allTabs = Children.toArray(props.children)
      .filter(child => child.type === Tab);
  
    return allTabs.map(tab => getSubcomponent(tab, componentType));
  };

  const renderTabContent = () => {
    const tabContents = getAllSubcomponentsOfType(TabContent);
    return tabContents[selectedIndex];
  };
  
  const cloneWithAdditionalProps = (tabComponent, index) => cloneElement(tabComponent, {
    isSelected: selectedIndex === index,
    onSelect: () => onTabSelect(index),
    key: `tabTitle${index}`,
  });

  const renderTabTitles = () => {
    const tabTitles = getAllSubcomponentsOfType(TabTitle);

    return tabTitles.map(cloneWithAdditionalProps);
  };
  
  return (
    <div className="TabbedContent">
      <div className="TabbedContent__tabTitles">
        {renderTabTitles()}
      </div>
      <div className="TabbedContent__tabContent">
        {renderTabContent()}
      </div>
    </div>
  );
}

TabbedContent.propTypes = {
  children: PropTypes.node,
};

export default TabbedContent;
